/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { XError } from '@wit/vtv-x-foundation-lib';
import LocalizedStringsFactory, { LocalizedStrings } from 'react-localization';
import TranslationKeys from './TranslationKeys';

// the object 'langsConfig' (loaded by a static script in index.html) is available in the global scope
// LangsConfig.d.ts

function calcSelectedLang(uiLang: string, configs: LangsConfig): string {
  // lang examples: 'en', 'en-EN', 'en-GB'

  const supportedLangs = configs.supported;

  // split examples:
  // 'en-EN' -> ['en', 'en']
  // 'en' -> ['en']
  const uiLangParts = uiLang.toLocaleLowerCase().split(/[_-]/);

  let perfectMatch; // examples: 'en'->'en' | 'en-EN'->'en-EN' | 'en'->'en-EN'
  let partialMatch; // examples: 'en'->'en-GB'

  supportedLangs.forEach((supportedLang) => {
    const supportedlangParts = supportedLang.toLocaleLowerCase().split(/[_-]/);

    if (
      (uiLangParts[0] === supportedlangParts[0] && uiLangParts[1] === supportedlangParts[1]) ||
      (uiLangParts[0] === supportedlangParts[0] && uiLangParts[0] === supportedlangParts[1]) ||
      (uiLangParts[0] === supportedlangParts[0] && uiLangParts[1] === supportedlangParts[0])
    ) {
      perfectMatch = supportedLang;
    } else if (uiLangParts[0] === supportedlangParts[0]) {
      partialMatch = supportedLang;
    }
  });

  return perfectMatch || partialMatch || configs.default;
}

async function getRemoteLangStrings(lang: string) {
  const resp = await fetch(`${process.env.PUBLIC_URL}/langs/${lang}.json`);
  if (resp.ok) {
    return resp.json();
  }
  return Promise.reject(XError.fromError(new Error('Error loading configs (i18n)'))); // TODO: create specific error code in configs errors map
}

// by default initialize the translate instance with an empty string map
// it is an immutable reference that will be exported for the other modules
const translate: LocalizedStrings<TranslationKeys> = new LocalizedStringsFactory({
  [langsConfig.default]: {} as TranslationKeys,
});

// initialize the translate service with the final localized strings (retrieved from the server)
export async function initTranslate() {
  // the 'translate' give us the browser lang
  const uiLang = translate.getInterfaceLanguage();

  const selectedLang = calcSelectedLang(uiLang, langsConfig);

  const translations = await getRemoteLangStrings(selectedLang);

  // after retrieve the strings for the selected lang
  // set them in the existing instance
  const localizedStrings = { [selectedLang]: translations };
  translate.setContent(localizedStrings);

  return localizedStrings;
}

export default translate;
